import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { formatKey } from "./assessmentFormatKey";
import {
  getNextEligibleFilters,
  kpiMatchesCascadeSteps,
  norm,
  parseCascadeTags,
} from "./kpiCascade";

export { parseCascadeTags, kpiMatchesCascadeSteps };

const KpiCascadeFilter = ({ kpiList = [], filterSteps = [], onFilterStepsChange, lockedSteps = [] }) => {
  const [openKey, setOpenKey] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpenKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const userActiveSteps = filterSteps.filter((s) => s.values?.size);
  const allActiveSteps = useMemo(
    () => [...lockedSteps, ...userActiveSteps],
    [lockedSteps, userActiveSteps]
  );

  const activeSteps = userActiveSteps;

  const eligibleNext = useMemo(
    () => getNextEligibleFilters(kpiList, allActiveSteps),
    [kpiList, allActiveSteps]
  );

  const nextKeys = useMemo(() => {
    const activeKeys = new Set(allActiveSteps.map((s) => norm(s.key)));
    return eligibleNext.filter((k) => !activeKeys.has(norm(k.key))).map((k) => k.key);
  }, [eligibleNext, allActiveSteps]);

  const openValues = useMemo(() => {
    if (!openKey) return [];

    const stepIdx = allActiveSteps.findIndex((s) => norm(s.key) === norm(openKey));
    const prefix = stepIdx >= 0 ? allActiveSteps.slice(0, stepIdx) : allActiveSteps;

    const vals = new Set();
    for (const item of kpiList) {
      for (const combo of parseCascadeTags(item.rawTags)) {
        let pos = 0;
        let ok = true;
        for (const step of prefix) {
          if (
            pos >= combo.length ||
            norm(combo[pos].key) !== norm(step.key) ||
            ![...step.values].some((sv) => norm(sv) === norm(combo[pos].value))
          ) {
            ok = false;
            break;
          }
          pos++;
        }
        if (ok && norm(combo[pos]?.key) === norm(openKey) && combo[pos]?.value) {
          vals.add(combo[pos].value);
        }
      }
    }
    return Array.from(vals).sort();
  }, [openKey, allActiveSteps, kpiList]);

  const openSelectedVals = useMemo(() => {
    const s = filterSteps.find((s) => norm(s.key) === norm(openKey));
    return s?.values ?? new Set();
  }, [filterSteps, openKey]);

  const getValueCounts = useCallback(
    (key, value) => {
      const stepIdx = allActiveSteps.findIndex((s) => norm(s.key) === norm(key));
      const prefix = stepIdx >= 0 ? allActiveSteps.slice(0, stepIdx) : allActiveSteps;

      const matching = kpiList.filter((item) => {
        for (const combo of parseCascadeTags(item.rawTags)) {
          let pos = 0;
          let ok = true;
          for (const step of prefix) {
            if (
              pos >= combo.length ||
              norm(combo[pos].key) !== norm(step.key) ||
              ![...step.values].some((sv) => norm(sv) === norm(combo[pos].value))
            ) {
              ok = false;
              break;
            }
            pos++;
          }
          if (
            ok &&
            norm(combo[pos]?.key) === norm(key) &&
            norm(combo[pos]?.value) === norm(value)
          ) return true;
        }
        return false;
      });

      const afterFilter = kpiList.filter((item) =>
        kpiMatchesCascadeSteps(item, allActiveSteps)
      );

      return {
        total: matching.length,
        selected: matching.filter((item) => afterFilter.some((f) => f.id === item.id)).length,
      };
    },
    [kpiList, allActiveSteps]
  );

  const matchCount = useMemo(
    () => kpiList.filter((item) => kpiMatchesCascadeSteps(item, allActiveSteps)).length,
    [kpiList, allActiveSteps]
  );

  const toggleValue = (key, value) => {
    const next = filterSteps.map((s) => ({ ...s, values: new Set(s.values) }));
    const idx = next.findIndex((s) => norm(s.key) === norm(key));

    if (idx === -1) {
      next.push({ key, values: new Set([value]) });
    } else {
      const vals = next[idx].values;
      if (vals.has(value)) vals.delete(value);
      else vals.add(value);

      if (!vals.size) {
        next.splice(idx, next.length - idx);
        setOpenKey(null);
      }
    }
    onFilterStepsChange?.(next);
  };

  const removeStep = (key) => {
    const idx = filterSteps.findIndex((s) => norm(s.key) === norm(key));
    if (idx >= 0) {
      onFilterStepsChange?.(filterSteps.slice(0, idx));
      setOpenKey(null);
    }
  };

  const clearAll = (e) => {
    e?.stopPropagation();
    onFilterStepsChange?.([]);
    setOpenKey(null);
  };

  const hasAny = lockedSteps.length > 0 || activeSteps.length > 0 || nextKeys.length > 0;

  const S = {
    chip: (isOpen, isActive) => ({
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      padding: "4px 8px 4px 10px",
      borderRadius: 7,
      cursor: "pointer",
      border: `1.5px solid ${isOpen ? "#3b82f6" : isActive ? "#93c5fd" : "#e2e8f0"}`,
      background: isOpen ? "#eff6ff" : isActive ? "#dbeafe" : "#fff",
      fontSize: 11,
      fontWeight: 600,
      color: isOpen || isActive ? "#1d4ed8" : "#64748b",
      transition: "all 0.15s",
      userSelect: "none",
    }),
    countBadge: (active) => ({
      fontSize: 9,
      fontWeight: 800,
      padding: "1px 5px",
      borderRadius: 8,
      marginLeft: 2,
      background: active ? "#bfdbfe" : "#f1f5f9",
      color: active ? "#1e40af" : "#475569",
    }),
    arrow: { color: "#bae6fd", fontSize: 15, lineHeight: 1, userSelect: "none" },
    dropdown: {
      position: "absolute",
      top: "calc(100% + 6px)",
      left: 0,
      zIndex: 600,
      background: "#fff",
      border: "1.5px solid #e5e7eb",
      borderRadius: 10,
      boxShadow: "0 4px 6px -1px rgba(0,0,0,.07), 0 16px 48px -4px rgba(0,0,0,.12)",
      minWidth: 240,
      overflow: "hidden",
    },
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", minHeight: 34 }}>

        <span style={{
          fontSize: 10, fontWeight: 700, color: "#94a3b8",
          textTransform: "uppercase", letterSpacing: "0.5px", flexShrink: 0,
        }}>
          Tag Filter:
        </span>

        {lockedSteps.map((step, i) => (
          <span key={`locked-${step.key}`} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            {i > 0 && <span style={S.arrow}>›</span>}
            <span style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "4px 10px",
              borderRadius: 7,
              border: "1.5px solid #c7d2fe",
              background: "#eef2ff",
              fontSize: 11,
              fontWeight: 600,
              color: "#4338ca",
              userSelect: "none",
            }}>
              🔒
              <span style={{ color: "#6366f1", fontWeight: 700 }}>{formatKey(step.key)}:</span>
              {Array.from(step.values).join(", ")}
            </span>
            {(activeSteps.length > 0 || nextKeys.length > 0) && (
              <span style={S.arrow}>›</span>
            )}
          </span>
        ))}

        {activeSteps.map((step, i) => {
          const isOpen = openKey === step.key;
          return (
            <span key={step.key} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              {i > 0 && <span style={S.arrow}>›</span>}
              <button
                type="button"
                onClick={() => setOpenKey(isOpen ? null : step.key)}
                style={S.chip(isOpen, true)}
              >
                <span style={{ color: "#3b82f6", fontWeight: 700 }}>{formatKey(step.key)}:</span>
                <span style={{ maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {Array.from(step.values).join(", ")}
                </span>
                <span style={S.countBadge(true)}>{matchCount}</span>
                <span style={{ fontSize: 9, color: "#93c5fd" }}>▾</span>
                <span
                  role="button"
                  onClick={(e) => { e.stopPropagation(); removeStep(step.key); }}
                  style={{
                    fontSize: 14, lineHeight: 1, color: "#93c5fd",
                    fontWeight: 900, marginLeft: 1, padding: "0 1px",
                    transition: "color 0.15s", cursor: "pointer",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#ef4444")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "#93c5fd")}
                >
                  ×
                </span>
              </button>
            </span>
          );
        })}

        {activeSteps.length > 0 && nextKeys.length > 0 && (
          <span style={S.arrow}>›</span>
        )}

        {nextKeys.map((key) => {
          const isOpen = openKey === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setOpenKey(isOpen ? null : key)}
              style={S.chip(isOpen, false)}
            >
              {formatKey(key)} <span style={{ fontSize: 9 }}>▾</span>
            </button>
          );
        })}

        {!hasAny && (
          <span style={{ fontSize: 11.5, color: "#9ca3af", fontStyle: "italic" }}>
            No tags available for current KPIs
          </span>
        )}

        {activeSteps.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            style={{
              fontSize: 10, color: "#ef4444", border: "none",
              background: "none", cursor: "pointer", fontWeight: 700, marginLeft: 4,
            }}
          >
            ✕ Clear
          </button>
        )}
      </div>

      {openKey && openValues.length > 0 && (
        <div style={S.dropdown}>
          <div style={{
            padding: "7px 12px",
            background: "#f8fafc",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
            <span style={{
              fontSize: 10, fontWeight: 800, color: "#475569",
              textTransform: "uppercase", letterSpacing: "0.6px",
            }}>
              {formatKey(openKey)}
            </span>
            <span style={{ fontSize: 9, color: "#94a3b8", fontWeight: 600 }}>selected / total</span>
          </div>

          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            {openValues.map((val) => {
              const { total, selected } = getValueCounts(openKey, val);
              const isChecked = openSelectedVals.has(val);
              return (
                <div
                  key={val}
                  onClick={() => toggleValue(openKey, val)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 12px",
                    cursor: "pointer",
                    background: isChecked ? "#f0f9ff" : "transparent",
                    borderBottom: "1px solid #f8fafc",
                    transition: "background 0.1s",
                  }}
                >
                  <div style={{
                    width: 15, height: 15, borderRadius: 3, flexShrink: 0,
                    border: `1.5px solid ${isChecked ? "#6366f1" : "#d1d5db"}`,
                    background: isChecked ? "#6366f1" : "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.12s",
                  }}>
                    {isChecked && (
                      <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                        <path
                          d="M1.5 4.5l2.5 2.5 4-4"
                          stroke="#fff"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>

                  <span style={{
                    flex: 1, fontSize: 12,
                    color: "#374151",
                    fontWeight: isChecked ? 600 : 400,
                  }}>
                    {val}
                  </span>

                  <span style={{
                    fontSize: 10, fontWeight: 700,
                    background: isChecked ? "#dbeafe" : "#f1f5f9",
                    color: isChecked ? "#1d4ed8" : "#64748b",
                    borderRadius: 10, padding: "2px 8px", whiteSpace: "nowrap",
                    border: `1px solid ${isChecked ? "#bfdbfe" : "#e2e8f0"}`,
                  }}>
                    {selected}/{total}
                  </span>
                </div>
              );
            })}
          </div>

          {activeSteps.length > 0 && (
            <div style={{
              padding: "7px 14px",
              borderTop: "1px solid #f3f4f6",
              background: "#f9fafb",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
              <span style={{ fontSize: 10.5, color: "#6b7280", fontWeight: 500 }}>
                {activeSteps.length} filter{activeSteps.length !== 1 ? "s" : ""} active
                {" · "}
                {matchCount} KPI{matchCount !== 1 ? "s" : ""} match
              </span>
              <button
                type="button"
                onClick={clearAll}
                style={{
                  fontSize: 10.5, color: "#6366f1", background: "none",
                  border: "none", cursor: "pointer", fontWeight: 700,
                  padding: 0, fontFamily: "inherit",
                }}
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default KpiCascadeFilter;